/**
 * "Period charges" on a payer's Settings (V1 completion G5, docs/features/v1-completion/
 * G_warehouse_services_charging_spec.md §4.5): what the payer is billed on a schedule with no order — storage,
 * a fulfilment service, a licence fee.
 *
 * Owned by the sales ledger and read from its composed view, so, like the catalogue section, it loads and acts on its
 * own and sits outside the page's draft and Save bar: every change here is a command that commits at once. The wording
 * (terms, status, periods) is the backend's and is rendered as it comes; the commands (Add, End, Withdraw, Record
 * usage) arrive with their URLs and whether they can run now, and run in the object UI's command dialog.
 *
 * Per charge: the terms, the operator, the last period billed (linking its invoice), the margin over the operator's
 * cost, and each ended period still waiting for usage with its Record usage button. A pick-and-pack period shows the
 * picks counted so far, the operator's cost of them and what it would bill; while any pick has no cost yet it says the
 * period waits. Staff only: nothing here reaches a customer.
 */
import { Link } from "react-router";
import type {
  PeriodAwaitingUsage,
  PeriodChargeRow,
} from "@shared-core/data/period-charges-types";
import type { Command } from "@shared-core/domain/object-ui/contract";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { usePeriodCharges } from "@shared-core/domain/period-charges";
import { paths } from "@shared-core/routing/routes";
import { useCommandHost } from "../../../useCommandHost";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Skeleton } from "../../primitives/skeleton";

export function PeriodChargesSection({
  billingAccountRef,
}: {
  /** The payer's billing account, e.g. `CIS:CUS-BRAM`. */
  billingAccountRef: string;
}) {
  const { load, reload } = usePeriodCharges(billingAccountRef);
  const index = useObjectManifest();
  const { openCommand, shell } = useCommandHost();

  const run = (command: Command, context: string) =>
    openCommand({
      endpoint: {
        command: index.commandOf(command.name),
        href: command.href,
        dryRunHref: command.dry_run_href,
      },
      context,
    });

  return (
    <section
      aria-label="Period charges"
      className="space-y-3 rounded-lg border border-border p-4 text-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold">Period charges</h2>
          <p className="text-xs text-muted-foreground">
            Billed on a schedule, not on an order.
          </p>
        </div>
        {load.kind === "loaded" &&
          load.value.commands.map((command) => (
            <Button
              key={command.name}
              type="button"
              size="sm"
              variant="outline"
              disabled={!command.enabled}
              title={command.reason ?? undefined}
              onClick={() => run(command, "Period charges")}
            >
              {command.label}
            </Button>
          ))}
      </div>

      {load.kind === "loading" && (
        <div
          role="status"
          aria-label="Loading period charges"
          className="space-y-2"
        >
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      )}

      {load.kind === "failed" && (
        <Alert variant="destructive" role="alert">
          <AlertDescription className="flex flex-wrap items-center gap-2">
            <span>Could not load the period charges: {load.message}</span>
            <Button type="button" size="sm" variant="outline" onClick={reload}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {load.kind === "loaded" && load.value.charges.length === 0 && (
        <p className="text-muted-foreground">
          No period charges yet. Add one to bill storage, a service or a licence
          on a schedule.
        </p>
      )}

      {load.kind === "loaded" && load.value.charges.length > 0 && (
        <ul className="divide-y divide-border">
          {load.value.charges.map((charge) => (
            <li key={charge.id} className="py-3 first:pt-0 last:pb-0">
              <ChargeRow charge={charge} run={run} />
            </li>
          ))}
        </ul>
      )}

      {shell}
    </section>
  );
}

function ChargeRow({
  charge,
  run,
}: {
  charge: PeriodChargeRow;
  run: (command: Command, context: string) => void;
}) {
  const isActive = charge.status === "active";
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <h3 className="font-medium">{charge.title}</h3>
        <Badge variant="outline">{charge.kind_label}</Badge>
        {!isActive && <Badge variant="secondary">{charge.status_label}</Badge>}
        <span className="flex-1" />
        {charge.commands.map((command) => (
          <Button
            key={command.name}
            type="button"
            size="sm"
            variant="ghost"
            disabled={!command.enabled}
            title={command.reason ?? undefined}
            onClick={() => run(command, charge.title)}
          >
            {command.label}
          </Button>
        ))}
      </div>
      <p>{charge.terms}</p>
      <dl className="grid gap-x-4 gap-y-1 text-xs text-muted-foreground sm:grid-cols-[max-content_1fr]">
        {charge.operator && (
          <>
            <dt>Operator</dt>
            <dd>
              {charge.operator.name} ({charge.operator.supplier_no})
            </dd>
          </>
        )}
        <dt>Last billed</dt>
        <dd>
          <LastBilled charge={charge} />
        </dd>
        {charge.margin && (
          <>
            <dt>Margin</dt>
            <dd>
              {charge.margin.operator_cost_pence === null ||
              charge.margin.difference_pence === null
                ? `Billed ${formatQuotedPrice(charge.margin.billed_net_pence)} net; the operator's cost is not recorded yet.`
                : `Billed ${formatQuotedPrice(charge.margin.billed_net_pence)} net, operator's cost ${formatQuotedPrice(charge.margin.operator_cost_pence)}: ${formatQuotedPrice(charge.margin.difference_pence)} over.`}
            </dd>
          </>
        )}
      </dl>
      {charge.awaiting_usage.length > 0 && (
        <ul
          aria-label={`${charge.title}: waiting for usage`}
          className="space-y-2"
        >
          {charge.awaiting_usage.map((period) => (
            <li key={period.period_start}>
              <AwaitingUsage period={period} charge={charge} run={run} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LastBilled({ charge }: { charge: PeriodChargeRow }) {
  const last = charge.last_billed;
  if (!last) return <>Not billed yet</>;
  return (
    <>
      {last.period_label}, {formatQuotedPrice(last.net_pence)} net
      {last.invoice_no ? (
        <>
          {", "}
          <Link
            to={paths.invoiceDetail({ invoiceNo: last.invoice_no })}
            className="text-primary underline-offset-2 hover:underline"
          >
            {last.invoice_no}
          </Link>
        </>
      ) : (
        ", on an invoice not issued yet"
      )}
    </>
  );
}

function AwaitingUsage({
  period,
  charge,
  run,
}: {
  period: PeriodAwaitingUsage;
  charge: PeriodChargeRow;
  run: (command: Command, context: string) => void;
}) {
  const { counted, record_usage: recordUsage } = period;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
      <div className="min-w-0 flex-1">
        <p className="font-medium">{period.label} is waiting for usage</p>
        {counted && (
          <p className="text-xs">
            {counted.picks.toLocaleString("en-GB")} picks counted, operator cost{" "}
            {formatQuotedPrice(counted.operator_cost_pence)}
            {counted.is_billable
              ? `, plus ${Number(counted.markup_pct)}%: ${formatQuotedPrice(counted.amount_pence)} net.`
              : `. ${counted.picks_without_cost.toLocaleString("en-GB")} of ${counted.total_picks.toLocaleString("en-GB")} picks have no operator cost yet, so the period waits.`}
          </p>
        )}
      </div>
      {recordUsage && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!recordUsage.enabled}
          title={recordUsage.reason ?? undefined}
          onClick={() => run(recordUsage, `${charge.title}, ${period.label}`)}
        >
          {recordUsage.label}
        </Button>
      )}
    </div>
  );
}
